"use client";

import { useCallback, useContext, useEffect, useMemo } from "react";
import {
    FeedRidContext,
    PrematchFeedContext,
    useWebsocketUpdates,
    WebsocketContext,
} from "@finbackoffice/site-core";
import { HoursRange, IClientError } from "@finbackoffice/websocket-client";
import { PrematchContext } from "contexts";
import { FeedScopeEnum } from "utils/constants";
import { useGetCurrentFeedType, useSocketStorage } from "hooks";

// NOTICE: RUN ONLY ONCE
export const usePrematchSports = (): void => {
    const { setPrematch } = useContext(PrematchFeedContext);
    const { connected } = useContext(WebsocketContext);
    const { hoursRange } = useContext(PrematchContext);
    const { prematchRid } = useContext(FeedRidContext);
    const { getSports, getTopLeagues } = useSocketStorage(prematchRid);
    const feed = useGetCurrentFeedType();

    const handleSetPrematch = useCallback(
        (response: { rid: string }, error: IClientError) => {
            if (!error) {
                const rid = response.rid;
                const sports = getSports(rid);
                const topLeagues = getTopLeagues(rid);
                setPrematch({
                    rid,
                    sports,
                    topLeagues,
                    loading: false,
                    error: "",
                });
            } else {
                console.error(error);

                setPrematch((state) => ({
                    ...state,
                    loading: false,
                    error: error.error,
                }));
            }
        },
        [getSports, getTopLeagues, setPrematch],
    );

    const payload = useMemo(() => ({ hours: hoursRange || HoursRange.ALL }), [hoursRange]);

    const { subscribe, unsubscribe } = useWebsocketUpdates(
        "prematch",
        handleSetPrematch,
        { subscribeByDefault: false },
        payload,
    );

    useEffect(() => {
        if (feed === FeedScopeEnum.prematch) {
            subscribe();
        } else {
            unsubscribe();
            setPrematch({ rid: "", sports: [], loading: true, topLeagues: [], error: "" });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, hoursRange, feed]);

    useEffect(() => {
        return () => {
            if (connected) {
                setPrematch({ rid: "", sports: [], loading: true, topLeagues: [], error: "" });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hoursRange, connected]);

    useEffect(() => {
        return () => {
            if (feed === FeedScopeEnum.prematch) {
                setPrematch({ rid: "", sports: [], loading: true, topLeagues: [], error: "" });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
